// Story 7.6 — shared clip-card base for twitch_clip / youtube_short /
// kick_clip. The linked creator claims the top-left author slot (it's their
// clip); the platform name sits top-right as the subject/source marker.
// Epic 8 — rendered on parchment (ParchmentPost); the embed / link-out
// affordance lives in the parchment body.
//
// Embed contract: Story 7.8's ingestion payload carries `embed_url` in
// `source_specific` ({ clip_id, broadcaster_login, duration_seconds,
// view_count, embed_url }). Twitch embeds additionally REQUIRE a
// `parent=<hostname>` param appended at render time. When no embed_url
// is present (pre-7.8 seeds), the card degrades to the honest link-out
// CTA — never an iframe wrapping the source page (AC 8).

import type { ReactNode } from "react";

import { ParchmentPost } from "@greenhold/components";

import {
  ProfileLink,
  parchmentCtaClass,
  relativeTime,
  sourceDisplayName,
  wearSeed,
} from "./feedCardShared";
import type { FeedCard } from "./useFeed";

type ClipPayload = {
  embed_url?: string;
  broadcaster_login?: string;
  // Story 7.15 popular-by-game payload (arbitrary broadcaster, no creator).
  broadcaster_name?: string;
  game_name?: string;
  // Story 7.16 — machine translation of a foreign title (original kept).
  title_translated?: string;
  detected_lang?: string;
};

function clipPayload(card: FeedCard): ClipPayload {
  if (card.source_specific && typeof card.source_specific === "object") {
    return card.source_specific as ClipPayload;
  }
  return {};
}

/** Twitch embeds refuse to load without `parent=<embedding hostname>`. */
function embedSrc(kind: string, embedUrl: string): string {
  if (kind !== "twitch_clip" && kind !== "twitch_popular") return embedUrl;
  const sep = embedUrl.includes("?") ? "&" : "?";
  const host =
    typeof window !== "undefined" ? window.location.hostname : "localhost";
  return `${embedUrl}${sep}parent=${host}&autoplay=false`;
}

export function ClipCard({ card }: { card: FeedCard }) {
  const platform = sourceDisplayName(card.source_kind);
  const payload = clipPayload(card);
  const handle = card.creator_handle;

  // Story 7.15: popular-by-game clips have NO Greenhold creator — the
  // top-left slot attributes the broadcaster ("clip by X"), the subject
  // slot labels the game ("Popular · CS2"), and there is no profile link.
  // Story 7.15 (Twitch) + Story 7.23 (YouTube curated channels): both
  // are creator-agnostic and carry the same broadcaster_name/game_name
  // payload keys, so they share this branch.
  const isPopular =
    card.source_kind === "twitch_popular" ||
    card.source_kind === "youtube_popular";

  let author: ReactNode;
  let subject = platform;
  let provenanceName: string;

  if (isPopular) {
    const broadcaster = payload.broadcaster_name ?? platform;
    author = `clip by ${broadcaster}`;
    subject = payload.game_name ? `Popular · ${payload.game_name}` : platform;
    provenanceName = `${platform} · ${broadcaster}`;
  } else {
    const attribution =
      payload.broadcaster_login ??
      handle ??
      card.creator_display_name ??
      platform;
    const authorLabel =
      card.creator_display_name ??
      payload.broadcaster_login ??
      (handle ? `@${handle}` : platform);
    author = handle ? (
      <ProfileLink handle={handle} label={authorLabel} />
    ) : (
      authorLabel
    );
    provenanceName = `${platform} · ${attribution}`;
  }

  return (
    <ParchmentPost
      author={author}
      subject={subject}
      provenance="via"
      provenanceName={provenanceName}
      time={relativeTime(card.published_at)}
      pinned={card.is_pinned}
      seed={wearSeed(card.id)}
    >
      {payload.embed_url ? (
        <div className="aspect-video w-full overflow-hidden rounded">
          <iframe
            src={embedSrc(card.source_kind, payload.embed_url)}
            title={card.title}
            allowFullScreen
            className="h-full w-full border-0"
            data-testid="clip-embed"
          />
        </div>
      ) : null}
      <p className="m-0 mt-3 font-heading text-base font-semibold text-brand-ink-900 first:mt-0">
        {card.title}
      </p>
      {payload.title_translated ? (
        <p
          className="m-0 mt-1 font-body text-xs italic text-brand-ink-700/70"
          data-testid="clip-title-translated"
        >
          AI translated: {payload.title_translated}
        </p>
      ) : null}
      {!payload.embed_url && card.external_url ? (
        <p className="m-0 mt-2">
          <a
            href={card.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className={parchmentCtaClass}
            data-testid="card-link-out"
          >
            Watch on {platform} →
          </a>
        </p>
      ) : null}
    </ParchmentPost>
  );
}
